import { CourseRef, CourseStatus, DriverRef, PartnerCompanyRef, SharedColumnsResponse, TruckRef, deserializeShared } from './Course.entity';

/** ダイヤ（log_web_issues#361）。一覧の 1 行 */
export type TimetableEntity = {
  id: number;
  name: string;
  courseCount: number;
  createdAt: string;
  updatedAt: string;
};

export type TimetableCourseEntity = {
  id: number; code: string; name: string | null; tripNo: number; status: CourseStatus;
  firstTripCourse: CourseRef | null; defaultTruck: TruckRef | null; defaultDriver: DriverRef | null; partnerCompany: PartnerCompanyRef | null;
};
/** ダイヤの所属行（sort_order 順）。placeNames は対象外・適用終了を除いた訪問順の地点名（他社へ移った地点は null） */
export type TimetableCourseRouteEntity = { id: number; sortOrder: number; stopCount: number; placeNames: (string | null)[]; course: TimetableCourseEntity };
export type TimetableDetailEntity = TimetableEntity & { courseRoutes: TimetableCourseRouteEntity[] };
/** ダイヤに入れるときのコピー元の候補（GET /courses/:id/route_sources） */
export type RouteSourceEntity = { timetableId: number; timetableName: string | null; stopCount: number; placeNames: (string | null)[] };
export type RouteSourcesEntity = { unassigned: boolean; identical: boolean; sources: RouteSourceEntity[] };
/** PATCH/POST /timetables。course_routes は配列順が並び。省略は所属を触らない */
export type TimetablePayload = { name?: string; course_routes?: { course_id: number; copy_from_timetable_id?: number }[] };

export type TimetableResponse = { id: number; name: string; course_count: number; created_at: string; updated_at: string };
export type TimetableDetailResponse = TimetableResponse & {
  course_routes: { id: number; sort_order: number; stop_count: number; place_names: (string | null)[];
    course: SharedColumnsResponse & { id: number; code: string; name: string | null; trip_no: number; status: CourseStatus; first_trip_course: CourseRef | null } }[];
};
export type RouteSourceResponse = { timetable_id: number; timetable_name: string | null; stop_count: number; place_names: (string | null)[] };
export type RouteSourcesResponse = { unassigned: boolean; identical: boolean; sources: RouteSourceResponse[] };

export const deserializeTimetable = (data: TimetableResponse): TimetableEntity => ({
  id: data.id, name: data.name, courseCount: data.course_count, createdAt: data.created_at, updatedAt: data.updated_at,
});
export const deserializeTimetableDetail = (data: TimetableDetailResponse): TimetableDetailEntity => ({
  ...deserializeTimetable(data),
  courseRoutes: data.course_routes.map((r) => ({
    id: r.id, sortOrder: r.sort_order, stopCount: r.stop_count, placeNames: r.place_names,
    course: { id: r.course.id, code: r.course.code, name: r.course.name, tripNo: r.course.trip_no, status: r.course.status,
      firstTripCourse: r.course.first_trip_course, ...deserializeShared(r.course) },
  })),
});
export const deserializeRouteSource = (s: RouteSourceResponse): RouteSourceEntity => ({
  timetableId: s.timetable_id, timetableName: s.timetable_name, stopCount: s.stop_count, placeNames: s.place_names,
});
export const deserializeRouteSources = (data: RouteSourcesResponse): RouteSourcesEntity => ({
  unassigned: data.unassigned, identical: data.identical, sources: data.sources.map(deserializeRouteSource),
});

// ---- 便グループ（GET /timetables/:id/trip_groups。log_web_issues#365）----
// 反映の車両枠 1 つ = 便グループ（1 便の行と、それに紐付く 2 便以降）。カード配車の要確認・本日なし・ダイヤ外・placeholder 行の材料

export type TimetableTripStopEntity = { rotationNo: number; sequenceNo: number; stopType: 'loading' | 'unloading'; placeName: string };
export type TimetableTripEntity = { courseId: number; courseCode: string; tripNo: number };
export type TimetableTripGroupEntity = {
  /** 1 便の行の id */
  id: number;
  truckId: number | null;
  driverId: number | null;
  /** 1 便のルートの sort_order */
  sortOrder: number;
  /** 有効な便（trip_no 順） */
  trips: TimetableTripEntity[];
  /** 全便の地点を tripNo → sequenceNo 順に平らにしたもの。rotationNo = tripNo（当日の cycle）。vehicleRowVM / needsReview が (truckId, rotationNo) で引く */
  stops: TimetableTripStopEntity[];
};
export type TimetableTruckMissingEntity = { courseId: number; courseCode: string; tripNo: number };
export type TimetableTripGroupsEntity = {
  timetableId: number;
  planningDate: string;
  tripGroups: TimetableTripGroupEntity[];
  /** トラックの無い便グループ（反映で飛ばされる。tripGroups にも truckId: null で入る） */
  truckMissing: TimetableTruckMissingEntity[];
};

export type TimetableTripGroupsResponse = {
  timetable_id: number;
  planning_date: string;
  trip_groups: {
    id: number; truck_id: number | null; driver_id: number | null; sort_order: number;
    trips: { course: { id: number; code: string; trip_no: number }; stops: { sequence_no: number; stop_type: 'loading' | 'unloading'; place_name: string }[] }[];
  }[];
  truck_missing: { course_id: number; course_code: string; trip_no: number }[];
};

export const deserializeTimetableTripGroups = (data: TimetableTripGroupsResponse): TimetableTripGroupsEntity => ({
  timetableId: data.timetable_id,
  planningDate: data.planning_date,
  tripGroups: data.trip_groups.map((g) => ({
    id: g.id, truckId: g.truck_id, driverId: g.driver_id, sortOrder: g.sort_order,
    trips: g.trips.map((t) => ({ courseId: t.course.id, courseCode: t.course.code, tripNo: t.course.trip_no })),
    stops: g.trips.flatMap((t) => t.stops.map((s) => ({
      rotationNo: t.course.trip_no, sequenceNo: s.sequence_no, stopType: s.stop_type, placeName: s.place_name,
    }))),
  })),
  truckMissing: data.truck_missing.map((w) => ({ courseId: w.course_id, courseCode: w.course_code, tripNo: w.trip_no })),
});
